import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services/authService";
import Logo from "./Logo";
import {
  Menu,
  X,
  LayoutDashboard,
  LogOut,
  Lock,
  Eye,
  EyeOff,
  Check,
} from "lucide-react";
import { useViewport } from "../../hooks/useViewport";
import toast from "react-hot-toast";

// ─── Change Password Modal ────────────────────────────────────────────────────

const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleShow = (k) => setShow((p) => ({ ...p, [k]: !p[k] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6)
      return toast.error("New password must be at least 6 characters.");
    if (form.next !== form.confirm)
      return toast.error("New passwords do not match.");
    setSaving(true);
    try {
      await authService.changePassword(form.current, form.next);
      toast.success("Password changed successfully!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to change password.");
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1.5px solid #e5e7eb",
    backgroundColor: "#fff",
  };
  const inputStyle = {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
    color: "#1f2937",
    backgroundColor: "transparent",
  };
  const eyeBtn = {
    border: "none",
    background: "none",
    cursor: "pointer",
    padding: 2,
    color: "#9ca3af",
    display: "flex",
    flexShrink: 0,
  };
  const label = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                backgroundColor: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Lock size={17} color="#16a34a" />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1f2937",
                  fontFamily: "'Playfair Display', serif",
                }}
              >
                Change Password
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                Keep your account secure
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: 6,
              borderRadius: 8,
              color: "#6b7280",
              display: "flex",
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            padding: "20px 24px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {[
            {
              key: "current",
              label: "Current Password",
              placeholder: "Enter current password",
            },
            {
              key: "next",
              label: "New Password",
              placeholder: "Min. 6 characters",
            },
            {
              key: "confirm",
              label: "Confirm New Password",
              placeholder: "Repeat new password",
            },
          ].map(({ key, label: lbl, placeholder }) => (
            <div key={key}>
              <label style={label}>{lbl}</label>
              <div style={fieldStyle}>
                <Lock size={15} color="#9ca3af" />
                <input
                  type={show[key] ? "text" : "password"}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  style={inputStyle}
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleShow(key)}
                  style={eyeBtn}
                >
                  {show[key] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: "11px",
                borderRadius: 12,
                border: "1.5px solid #e5e7eb",
                backgroundColor: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                padding: "11px",
                borderRadius: 12,
                border: "none",
                backgroundColor: "#16a34a",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                opacity: saving ? 0.75 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
              }}
            >
              {saving ? (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid rgba(255,255,255,0.35)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <Check size={15} />
              )}
              {saving ? "Saving..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/rooms", label: "Rooms" },
  { to: "/gallery", label: "Gallery" },
  { to: "/#how-to-reach", label: "How to Reach", isAnchor: true },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const dashboardPath =
    user?.user_type === "staff"
      ? "/owner/dashboard"
      : `/${user?.user_type}/dashboard`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const location = useLocation();
  const { isDesktop } = useViewport();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
  }, [isDesktop]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const navStyle = {
    position: "sticky",
    top: 0,
    zIndex: 50,
    backgroundColor: "#ffffff",
    boxShadow: scrolled
      ? "0 4px 20px rgba(0,0,0,0.1)"
      : "0 1px 4px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.3s ease",
  };

  const containerStyle = {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "0 24px",
    height: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const logoStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none",
  };

  const desktopNavStyle = {
    display: "flex",
    alignItems: "center",
    gap: 4,
  };

  const getLinkStyle = (to) => ({
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: "none",
    transition: "all 0.2s",
    backgroundColor: to && isActive(to) ? "#16a34a" : "transparent",
    color: to && isActive(to) ? "#ffffff" : "#374151",
    cursor: "pointer",
  });

  const authBtnStyle = (primary) => ({
    padding: "7px 18px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: "none",
    border: primary ? "none" : "1.5px solid #e5e7eb",
    backgroundColor: primary ? "#16a34a" : "transparent",
    color: primary ? "#ffffff" : "#374151",
    cursor: "pointer",
    boxShadow: primary ? "0 2px 8px rgba(22,163,74,0.3)" : "none",
  });

  const avatarBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1.5px solid #e5e7eb",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    position: "relative",
  };

  const avatarCircleStyle = {
    width: 30,
    height: 30,
    borderRadius: "50%",
    backgroundColor: "#16a34a",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  };

  const dropdownMenuStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    border: "1px solid #e5e7eb",
    minWidth: 200,
    padding: "8px",
    zIndex: 100,
  };

  const dropdownItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    textDecoration: "none",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    width: "100%",
    textAlign: "left",
  };

  const mobileMenuStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e5e7eb",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    zIndex: 50,
    padding: "12px 16px 20px",
    animation: "fadeIn 0.2s ease-out",
  };

  return (
    <>
      {showChangePwd && (
        <ChangePasswordModal onClose={() => setShowChangePwd(false)} />
      )}
      <nav style={navStyle}>
        <div style={containerStyle}>
          {/* Logo */}
          <Link to="/" style={logoStyle}>
            <Logo height={44} />
          </Link>

          {/* Desktop Nav */}
          <div
            style={{ ...desktopNavStyle, display: isDesktop ? "flex" : "none" }}
          >
            {NAV_LINKS.map(({ to, label, isAnchor }) =>
              isAnchor ? (
                <a key={to} href={to} style={getLinkStyle(null)}>
                  {label}
                </a>
              ) : (
                <Link key={to} to={to} style={getLinkStyle(to)}>
                  {label}
                </Link>
              )
            )}
          </div>

          {/* Desktop Auth */}
          <div
            style={{
              display: isDesktop ? "flex" : "none",
              alignItems: "center",
              gap: 8,
            }}
          >
            {isAuthenticated ? (
              <div style={{ position: "relative" }}>
                <button
                  style={avatarBtnStyle}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <div style={avatarCircleStyle}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <span
                    style={{
                      maxWidth: 110,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user?.name}
                  </span>
                </button>
                {dropdownOpen && (
                  <div style={dropdownMenuStyle}>
                    <div
                      style={{
                        padding: "6px 12px 10px",
                        borderBottom: "1px solid #f3f4f6",
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontWeight: 600,
                        }}
                      >
                        {user?.user_type}
                      </span>
                    </div>
                    <Link to={dashboardPath} style={dropdownItemStyle}>
                      <LayoutDashboard size={15} color="#16a34a" />
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        setShowChangePwd(true);
                      }}
                      style={dropdownItemStyle}
                    >
                      <Lock size={15} color="#6b7280" />
                      Change Password
                    </button>
                    <button
                      onClick={logout}
                      style={{ ...dropdownItemStyle, color: "#dc2626" }}
                    >
                      <LogOut size={15} color="#dc2626" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" style={authBtnStyle(false)}>
                  Sign In
                </Link>
                <Link to="/register" style={authBtnStyle(true)}>
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: isDesktop ? "none" : "flex",
              padding: 8,
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              borderRadius: 8,
              alignItems: "center",
            }}
          >
            {menuOpen ? (
              <X size={22} color="#374151" />
            ) : (
              <Menu size={22} color="#374151" />
            )}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {menuOpen && !isDesktop && (
          <div style={mobileMenuStyle}>
            {NAV_LINKS.map(({ to, label, isAnchor }) => {
              const mobileStyle = {
                display: "block",
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                color: !isAnchor && isActive(to) ? "#ffffff" : "#374151",
                backgroundColor: !isAnchor && isActive(to) ? "#16a34a" : "transparent",
                marginBottom: 2,
                textDecoration: "none",
              };
              return isAnchor ? (
                <a key={to} href={to} style={mobileStyle} onClick={() => setMenuOpen(false)}>
                  {label}
                </a>
              ) : (
                <Link key={to} to={to} style={mobileStyle}>
                  {label}
                </Link>
              );
            })}
            <div style={{ borderTop: "1px solid #e5e7eb", margin: "10px 0" }} />
            {isAuthenticated ? (
              <>
                <Link
                  to={dashboardPath}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#374151",
                    textDecoration: "none",
                  }}
                >
                  <LayoutDashboard size={16} color="#16a34a" /> Dashboard
                </Link>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setShowChangePwd(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#374151",
                    border: "none",
                    backgroundColor: "transparent",
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <Lock size={16} color="#6b7280" /> Change Password
                </button>
                <button
                  onClick={logout}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#dc2626",
                    border: "none",
                    backgroundColor: "transparent",
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <LogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  style={{
                    display: "block",
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#374151",
                    textDecoration: "none",
                    textAlign: "center",
                    border: "1.5px solid #e5e7eb",
                    marginBottom: 6,
                  }}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  style={{
                    display: "block",
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#ffffff",
                    backgroundColor: "#16a34a",
                    textDecoration: "none",
                    textAlign: "center",
                  }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
